// Season math for Workboard Halloween. No DOM and no browser APIs.

export const RAMP_DAYS = 14;

export const SHOW = {
  web: 10,
  spider: 7,
  pumpkin: 5,
  bats: 5,
  ghost: 0,
} as const;

export type HalloweenLevel = 'off' | 'subtle' | 'full';

export type CardKind = 'web' | 'pumpkin' | 'spider' | 'bat';

export type SpookKind = CardKind | 'cursed';

export interface CardPlan {
  share: number;
  cursed: boolean;
}

export interface SeasonIntensity {
  days: number;
  t: number;
  active: boolean;
}

const CARD_KINDS: readonly CardKind[] = ['web', 'pumpkin', 'spider', 'bat'];

export const LEVEL_CAP: Record<HalloweenLevel, number> = {
  off: 0,
  subtle: 0.7,
  full: 1,
};

const PALETTE = {
  nav: { from: [208, 97, 31], to: [272, 45, 20] },
  accent: { from: [208, 97, 46], to: [26, 90, 50] },
  paper: { from: [36, 0, 94], to: [36, 45, 91] },
};

type Hsl = readonly [number, number, number];

export const daysToHalloween = (now: Date): number => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const halloween = new Date(now.getFullYear(), 9, 31);
  return Math.round((halloween.getTime() - today.getTime()) / 86400000);
};

export const intensity = (now: Date, previewDays: number | null): SeasonIntensity => {
  const days = previewDays != null && previewDays >= 0 ? previewDays : daysToHalloween(now);
  if (days < 0 || days > RAMP_DAYS) {
    return { days, t: 0, active: false };
  }
  return { days, t: (RAMP_DAYS - days) / RAMP_DAYS, active: true };
};

const blendHsl = (from: Hsl, to: Hsl, t: number): string => {
  let dh = to[0] - from[0];
  if (dh < 0) {
    dh += 360;
  }
  const h = (from[0] + dh * t) % 360;
  const s = from[1] + (to[1] - from[1]) * t;
  const l = from[2] + (to[2] - from[2]) * t;
  return `hsl(${h.toFixed(1)} ${s.toFixed(1)}% ${l.toFixed(1)}%)`;
};

export const hslToHex = (hsl: string): string => {
  const match = /^hsl\(\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*\)$/.exec(hsl);
  if (!match) {
    return hsl;
  }
  const h = Number(match[1]) / 360;
  const s = Number(match[2]) / 100;
  const l = Number(match[3]) / 100;
  const hueToRgb = (p: number, q: number, t: number) => {
    let channel = t;
    if (channel < 0) {
      channel += 1;
    }
    if (channel > 1) {
      channel -= 1;
    }
    if (channel < 1 / 6) {
      return p + (q - p) * 6 * channel;
    }
    if (channel < 1 / 2) {
      return q;
    }
    if (channel < 2 / 3) {
      return p + (q - p) * (2 / 3 - channel) * 6;
    }
    return p;
  };

  let red = l;
  let green = l;
  let blue = l;
  if (s !== 0) {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    red = hueToRgb(p, q, h + 1 / 3);
    green = hueToRgb(p, q, h);
    blue = hueToRgb(p, q, h - 1 / 3);
  }
  const byte = (channel: number) => Math.round(channel * 255).toString(16).padStart(2, '0');
  return `#${byte(red)}${byte(green)}${byte(blue)}`;
};

export const colours = (t: number, level: HalloweenLevel) => {
  const k = t * (LEVEL_CAP[level] || 0);
  return {
    k,
    nav: blendHsl(PALETTE.nav.from, PALETTE.nav.to, k),
    accent: blendHsl(PALETTE.accent.from, PALETTE.accent.to, k),
    paper: blendHsl(PALETTE.paper.from, PALETTE.paper.to, k),
  };
};

// Scalar blue stays on the logo side. Night purple takes more of the bar as Oct 31 gets closer.
export const navGradient = (t: number, level: HalloweenLevel): string => {
  const blended = colours(t, level);
  const blue = 'rgb(2, 83, 154)';
  const hold = Math.round(85 - 70 * blended.k);
  return `linear-gradient(90deg, ${blue} 0%, ${blue} ${hold}%, ${blended.nav} 100%)`;
};

const HASH_MOD = 1_000_003;

// Stable per string for the day. The low part picks whether a card is decorated; the high part picks the decoration.
export const hash = (value: string): number => {
  let h = 0;
  for (let i = 0; i < value.length; i += 1) {
    h = (h * 33 + value.charCodeAt(i)) % HASH_MOD;
  }
  return h;
};

const decorationIndex = (value: number): number => Math.floor(value / 1000) % CARD_KINDS.length;

export const cardPlan = (days: number, level: HalloweenLevel): CardPlan => {
  if (days < 0 || days > 7 || !LEVEL_CAP[level]) {
    return { share: 0, cursed: false };
  }
  const share = (0.08 + (0.17 * (7 - days)) / 7) * (level === 'full' ? 1 : 0.6);
  return { share, cursed: days <= 3 };
};

export const cardKind = (key: string, day: number, plan: CardPlan): CardKind | null => {
  if (!plan.share) {
    return null;
  }
  const h = hash(`${key}|${day}`);
  if ((h % 1000) / 1000 >= plan.share) {
    return null;
  }
  return CARD_KINDS[decorationIndex(h)];
};

export const fallbackPick = (keys: readonly string[], days: number): { index: number; kind: CardKind } | null => {
  if (!keys.length) {
    return null;
  }
  let best = -1;
  let bestH = Infinity;
  keys.forEach((key, index) => {
    const h = hash(`pick|${key}|${days}`);
    if (h < bestH) {
      bestH = h;
      best = index;
    }
  });
  const kind = days > 7 ? 'web' : CARD_KINDS[decorationIndex(bestH)];
  return { index: best, kind };
};

export const assignCardKinds = (
  keys: readonly string[],
  days: number,
  level: HalloweenLevel,
): Map<string, SpookKind> => {
  const plan = cardPlan(days, level);
  const want = new Map<string, SpookKind>();
  let cursedKey: string | null = null;
  let best = Infinity;
  keys.forEach(key => {
    const kind = cardKind(key, days, plan);
    if (kind) {
      want.set(key, kind);
    }
    if (plan.cursed) {
      const h = hash(`cursed|${key}|${days}`);
      if (h < best) {
        best = h;
        cursedKey = key;
      }
    }
  });
  if (cursedKey) {
    want.set(cursedKey, 'cursed');
  }
  if (want.size === 0) {
    const pick = fallbackPick(keys, days);
    if (pick) {
      want.set(keys[pick.index], pick.kind);
    }
  }
  return want;
};

export const spookClassByKey = (
  keys: readonly string[],
  days: number,
  level: HalloweenLevel,
): Map<string, string> => {
  const classes = new Map<string, string>();
  assignCardKinds(keys, days, level).forEach((kind, key) => {
    classes.set(key, `wbh-spook-${kind}`);
  });
  return classes;
};

export const withSpookClass = (className: string | undefined, spookClass: string | undefined): string | undefined => {
  if (!spookClass) {
    return className;
  }
  return className ? `${className} ${spookClass}` : spookClass;
};
